import React, { useState, useEffect } from 'react';
import {
  IconEnhanceBadge,
  IconPlayAlt,
} from '@spotify-internal/encore-web/core';
import { useAppContext } from 'contexts/app-context';
import { useField } from 'react-final-form';
import * as Styled from '../AudioScriptPlaybackCard.styled';

const CTAImages = [
  '/images/gen-ai/cta-animation-1.png',
  '/images/gen-ai/cta-animation-2.png',
  '/images/gen-ai/cta-animation-3.png',
];

type CTAPlaybackCardProps = {
  minutesSinceGeneration: number;
  restartAudio: () => void;
};

const CTAPlaybackCard = ({
  minutesSinceGeneration,
  restartAudio,
}: CTAPlaybackCardProps) => {
  const [{ isLoggedIn }] = useAppContext();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get AI voice value from form to determine colors
  const {
    input: { value: aiVoice },
  } = useField('aiVoice');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % CTAImages.length);
    }, 3_000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Styled.Container variant="highlight" selectedVoice={aiVoice}>
      <Styled.GeneratedAt selectedVoice={aiVoice}>
        <IconEnhanceBadge size="small" />
        {minutesSinceGeneration > 0 ? (
          <p>Generated {minutesSinceGeneration} minutes ago</p>
        ) : (
          <p>Generated just now</p>
        )}
      </Styled.GeneratedAt>
      <Styled.Content>
        <Styled.Carousel className="carousel">
          {CTAImages.map((src, i) => (
            <Styled.CTAImage
              src={src}
              alt="Ads Manager voiceover AI screen"
              layout="fill"
              objectFit="contain"
              unoptimized={false}
              isActive={i === currentImageIndex}
            />
          ))}
        </Styled.Carousel>
        <Styled.CTACaption>
          Get full access to our creative tools when you create an Ads Manager
          account.
        </Styled.CTACaption>
        <Styled.CTAButton
          href={
            isLoggedIn
              ? 'https://adsmanager.spotify.com/campaigns?source=gen-ai'
              : 'https://adsmanager.spotify.com/signup?source=gen-ai'
          }
          target="_blank"
          data-testid={
            isLoggedIn
              ? 'gen-ai-audio-tool-goto-button'
              : 'gen-ai-audio-tool-login-button'
          }
        >
          {isLoggedIn ? 'Go to Ads Manager' : 'Sign up'}
        </Styled.CTAButton>
        <Styled.ListenAgainButton
          onClick={() => {
            restartAudio();
          }}
          selectedVoice={aiVoice}
        >
          <IconPlayAlt size="small" />
          <p>Listen to your ad again</p>
        </Styled.ListenAgainButton>
      </Styled.Content>
    </Styled.Container>
  );
};

export default CTAPlaybackCard;
